import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { normalizeText } from "../../lib/format";
import { orderRows } from "../../lib/selectors";
import type { AnyRecord, AppData } from "../../lib/types";

export function CustomersPage({ data }: { data: AppData }) {
  const [query, setQuery] = useState("");
  const rows = customerRows(data).filter((row) => {
    const text = normalizeText([row.customerLabel, row.lastOrder, row.status, row.nextAction].join(" "));
    return !query || text.includes(normalizeText(query));
  });
  const columns = useMemo<ColumnDef<AnyRecord>[]>(() => [
    {
      accessorKey: "customerLabel",
      header: "고객",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.customerLabel}</strong>
          <span>{row.original.lastOrder}</span>
        </div>
      ),
      size: 240
    },
    { accessorKey: "orderCount", header: "주문", cell: ({ row }) => row.original.orderCount },
    { accessorKey: "caseCount", header: "확인", cell: ({ row }) => row.original.caseCount },
    { accessorKey: "status", header: "상태", cell: ({ row }) => <StatusBadge tone={row.original.caseCount ? "warning" : "success"}>{row.original.status}</StatusBadge> },
    { accessorKey: "nextAction", header: "다음 작업", cell: ({ row }) => row.original.nextAction }
  ], []);

  return (
    <div className="page-stack">
      <section className="toolbar-panel">
        <div className="toolbar-row">
          <label className="search-field">
            검색
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="고객, 주문번호, 상태" />
          </label>
        </div>
      </section>
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>고객 확인 큐</h2>
            <p>주문, 반품/교환, 출고 보류 같은 고객 응대가 필요한 항목을 모읍니다.</p>
          </div>
        </div>
        <DataTable columns={columns} data={rows} getRowId={(row) => String(row.customerLabel)} emptyTitle="확인할 고객 항목이 없습니다." />
      </section>
    </div>
  );
}

function customerRows(data: AppData): AnyRecord[] {
  const orders = orderRows(data);
  const grouped = new Map<string, AnyRecord>();
  for (const order of orders) {
    const customerLabel = String(order.customerLabel || "고객 미확인");
    const current = grouped.get(customerLabel) || {
      customerLabel,
      orderCount: 0,
      caseCount: 0,
      lastOrder: "-",
      status: "정상",
      nextAction: "주문 이력 확인"
    };
    current.orderCount += 1;
    current.lastOrder = String(order.orderIdMasked || order.orderKey || "-");
    const bucket = String(order.statusBucket || "");
    if (bucket.includes("return") || bucket.includes("exchange") || bucket.includes("cancel") || bucket.includes("check") || bucket.includes("hold")) {
      current.caseCount += 1;
      current.status = "확인 필요";
      current.nextAction = "주문 케이스 확인";
    }
    grouped.set(customerLabel, current);
  }
  return [...grouped.values()];
}
